import { Component, h, Host, Prop, Element } from '@stencil/core';

/**
 * A full-width dropdown menu with navigation items and featured content section.
 * Features a two-column layout: navigation items on the left, featured content on the right.
 *
 * @slot navigation-group - {required}, {multiple}, <div> - Navigation groups (can be used multiple times)
 * @slot featured - {optional}, <me-heading> + content - Featured section with heading and content
 * @slot footer-action - <me-button variant="ghost" tag="a" icon="arrows--arrow-right" iconPosition="after" class="dropdown-menu-type-2__footer-action"> - Optional footer call-to-action
 * @example
 *   <dropdown-menu-type-2>
 *     <div slot="navigation-group"><me-button variant="ghost" tag="a" href="/features">Features</me-button></div>
 *     <div slot="navigation-group"><me-button variant="ghost" tag="a" href="/pricing">Pricing</me-button></div>
 *   </dropdown-menu-type-2>
 */
@Component({
  tag: 'dropdown-menu-type-2',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DropdownMenuType2 {
  @Element() el: HTMLElement;

  /**
   * Additional class modifiers for styling.
   * @type {string}
   */
  @Prop() modifiers?: string;

  componentDidRender() {
    // Wrap each element with slot="navigation-group" in navigation-item div
    // Find all elements with slot="navigation-group" in the light DOM
    const navigationGroups = Array.from(this.el.querySelectorAll('[slot="navigation-group"]')) as HTMLElement[];
    const navigationContainer = this.el.querySelector('.dropdown-menu-type-2__navigation');

    if (navigationContainer && navigationGroups.length > 0) {
      navigationGroups.forEach(group => {
        // Check if already wrapped
        if (group.parentElement?.classList.contains('dropdown-menu-type-2__navigation-item')) {
          return;
        }

        // Create wrapper div
        const wrapper = document.createElement('div');
        wrapper.className = 'dropdown-menu-type-2__navigation-item';

        // Insert wrapper before the group
        group.parentNode?.insertBefore(wrapper, group);

        // Move group into wrapper
        wrapper.appendChild(group);
      });
    }
  }

  render() {
    const classes = ['dropdown-menu-type-2', this.modifiers || ''].join(' ');

    return (
      <Host class={classes}>
        <div class="dropdown-menu-type-2__wrapper">
          <div class="dropdown-menu-type-2__container">
            <div class="dropdown-menu-type-2__content">
              <div class="dropdown-menu-type-2__navigation">
                <slot name="navigation-group"></slot>
              </div>

              <div class="dropdown-menu-type-2__featured">
                <slot name="featured"></slot>
              </div>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
